import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import {
    getErrorResponse,
    getTimeRelativeTime,
    getUpdatedSearchParams,
    parseSearchParams,
} from '../../../services/util'

import { twElementStyles } from '../../../twElementStyles'
import { usePopup } from '../../../context/popupContext'
import {
    RenderDefaultActions,
    RenderImageWizard,
    RenderStatus,
} from '../common'
import { CustomTable } from '../../../components/customTable'
import { FilterOptions } from '../../../components/customTable/custom-table-interfaces'
import { useNotificationBar } from '../../../context/notificationContext'
import { useRolesData } from '../../../hooks/role'
import { SmoothTransition } from '../../../components/pageTransition'
import { Unauthorized } from '../../Unauthorized'
import { PagePreloader } from '../../../components/pagePreloader'
import moment from 'moment'
import { useMemo } from 'react'
import ReadMoreText from 'components/readMore'
import {
    useDeleteCategoriesData,
    useGetCategoryData,
    useUpdateCategoryStatus,
} from 'hooks/category-manage'
import ViewCategoryDialog from './ViewCategory'
import { renderWidgetChips } from 'components/label/RenderLabels'
import { ConflictDetailsDrawer } from 'components/ConflictDetailsDrawer'
import { useBulkLabelGenerationContext } from 'context/bulkLabelGenerationContext'
import { useActiveAIPrompt } from 'hooks/ai-settings'
import { SparklesIcon } from '@heroicons/react/24/outline'

export interface Response {
    records: any[]
    pages?: number
    total?: number
    pagination?: Object
}
const CategoryList = () => {
    let [searchParams, setSearchParams] = useSearchParams()
    let filterParams: any = parseSearchParams(searchParams, 'filter')
    const notification = useNotificationBar()
    const navigate = useNavigate()
    let [addUpdate, setAddUpdate] = useState<any>(false)
    let [addUpdateInput, setaddUpdateInput] = useState<any>(null)
    let [editForm, setEditForm] = useState<any>(false)
    //view dailog
    const [openView, setOpenView] = useState(false)
    const [viewData, setViewData] = useState(null)
    const [viewId, setViewId] = useState(null)

    const [deleteConflict, setDeleteConflict] = useState<{
        message: string
        details: string[]
    } | null>(null)

    let popup = usePopup()
    let {
        data,
        isFetching: isLoading,
        refetch,
        isError,
        isSuccess,
        error,
        dataUpdatedAt,
    } = useGetCategoryData(filterParams, {
        keepPreviousData: true,
    })


    let { mutate: deleteCategory } = useDeleteCategoriesData()
    let { mutate: updateStatusCategory } = useUpdateCategoryStatus()
    const { startGeneration, isGenerating, currentJob } = useBulkLabelGenerationContext()
    const { data: activePrompt } = useActiveAIPrompt()

    const openAddUpdateDialog = (input: any, string: any) => {
        setaddUpdateInput(input)
        setAddUpdate(true)
        if (string === 'edit') {
            setEditForm(true)
        } else {
            setEditForm(false)
        }
    }

    let useFormHook = useForm({
        defaultValues: filterParams,
    })

    let responseData: any = useMemo(() => {
        if (!data?.data) return null

        const raw = data?.data

        return {
            ...raw,
            results: raw?.results?.map((item: any) => ({
                ...item,
                parent_id: item.parentId ?? null,
                parentCategoryName: item.parentCategoryName ?? '',
            })),
        }
    }, [data])

    const onDeleteAction = (row: any) => {
        popup.open({
            title: 'Are you sure ?',
            message: (
                <>
                    Do you want to delete <strong>{row?.name}</strong> category? This cannot be undone.
                </>
            ),
            type: 'confirm',
            onAction: (value: boolean) => {
                if (value) {
                    deleteCategory(
                        {
                            id: row?.id,
                        },
                        {
                            onSuccess: () => {
                                notification.push({
                                    title: `Category deleted !`,
                                    message: `Category ${row?.name} has been deleted successfully.`,
                                    type: 'info',
                                })
                                refetch()
                            },
                            onError: (error: any) => {
                                const response = error?.response?.data || {}
                                const statusCode = response?.statusCode
                                const message =
                                    response?.message || 'Something went wrong'
                                const details = Array.isArray(response?.details)
                                    ? response.details
                                    : []

                                if (statusCode === 409) {
                                    notification.push({
                                        title: 'Cannot Delete',
                                        message: (
                                            <div className="space-y-2">
                                                <p className="text-xs leading-snug text-slate-700 dark:text-slate-200">
                                                    {message}
                                                </p>
                                                <button
                                                    type="button"
                                                    className="text-xs font-semibold text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                                    onClick={() =>
                                                        setDeleteConflict({
                                                            message,
                                                            details,
                                                        })
                                                    }
                                                >
                                                    View Details
                                                </button>
                                            </div>
                                        ),
                                        type: 'error',
                                    })
                                    return
                                }

                                notification.push({
                                    title: 'Error',
                                    message,
                                    type: 'error',
                                })
                            },
                        }
                    )
                }
            },
            confirmButtonText: 'Delete',
            confirmButtonClass: twElementStyles.twButtonElegantRed,
        })
    }

    const handleBulkLabelGeneration = async () => {
        const selectedCategoryIds = Object.keys(rowSelectionState[0] || {})

        if (selectedCategoryIds.length === 0) {
            notification.push({
                title: 'Info',
                type: 'info',
                message: 'Please select at least one category for bulk label generation.'
            })
            return
        }

        if (!activePrompt) {
            notification.push({
                title: 'Error',
                type: 'error',
                message: 'No active AI prompt found. Please configure one in Settings.'
            })
            return
        }

        popup.open({
            title: 'Confirm Bulk Label Generation',
            message: (
                <>
                    <p className="mb-2">
                        You are about to generate labels for <strong>{selectedCategoryIds.length}</strong> category(ies).
                    </p>
                    <p className="mt-2">Are you sure you want to continue?</p>
                </>
            ),
            type: 'confirm',
            onAction: async (confirmed: boolean) => {
                if (!confirmed) return

                startGeneration(selectedCategoryIds, activePrompt.prompt, 'category')
                rowSelectionState[1]({})
            },
            confirmButtonText: 'Generate Labels',
            confirmButtonClass: twElementStyles.twButtonElegant,
        })
    }

    useEffect(() => {
        if (currentJob?.status === 'completed' && currentJob?.entityType === 'category') {
            refetch()
        }
    }, [currentJob, refetch])

    const onStatusChange = async (row: any) => {
        let update: any = { status: row.status ? 0 : 1 }
        popup.open({
            title: `Are you sure ?`,
            message: `Do you want to ${update.status ? 'Activate' : 'Deactivate'
                } ${row.name} Category?`,
            type: 'confirm',
            onAction: (value: any) => {
                if (value) {
                    updateStatusCategory(
                        {
                            data: update,
                            id: row.id,
                        },
                        {
                            onSuccess: () => {
                                notification.push({
                                    title: `Category ${update.status
                                            ? 'activation success !'
                                            : 'deactivated !'
                                        } `,
                                    message: `${row.name} Category ${update.status
                                            ? 'activated'
                                            : 'deactivated'
                                        } successfully !`,
                                    type: update.status ? 'success' : 'info',
                                })
                                refetch()
                            },
                            onError: (error) => {
                                notification.push(getErrorResponse(error))
                            },
                        }
                    )
                }
            },
        })
    }

    const onAction = (actionType: any, row: any) => {
        if (actionType === 'edit') {
            navigate(`/category/update/${row?.id}`)
        }

        if (actionType === 'view') {
            const fullRow = responseData?.results?.find(
                (item) => item.id === row.id
            )
            setViewData(fullRow || null)
            setOpenView(true)
        }

        if (actionType === 'delete') onDeleteAction(row)
    }

    const columns: ColumnDef<any>[] = [
        {
            id: 'select',
            header: ({ table }) => {
                const isAllSelected = table.getIsAllRowsSelected()
                const isSomeSelected = table.getIsSomeRowsSelected()

                return (
                    <input
                        type="checkbox"
                        checked={isAllSelected}
                        ref={(input) => {
                            if (input) {
                                input.indeterminate = !isAllSelected && isSomeSelected
                            }
                        }}
                        onChange={table.getToggleAllRowsSelectedHandler()}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                )
            },
            cell: ({ row }) => (
                <input
                    type="checkbox"
                    checked={row.getIsSelected()}
                    disabled={!row.getCanSelect()}
                    onChange={row.getToggleSelectedHandler()}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
            ),
            enableSorting: false,
        },
        {
            accessorKey: 'id',
            header: () => 'ID',
            footer: (info) => info.column.id,
            cell: ({ getValue }) => {
                const id = getValue() as string
                return (
                    <div className="font-mono text-xs">
                        {id}
                    </div>
                )
            },
            enableSorting: false,
        },
        {
            id: 'image',
            header: 'Icon',
            accessorKey: 'icon',
            cell: ({ row }) =>
                RenderImageWizard({
                    row,
                    accessorKey: 'icon',
                    className: 'w-20 h-12 object-cover rounded overflow-hidden',
                }),
            enableSorting: false,
        },

        {
            accessorKey: 'name',
            header: () => 'NAME',
            footer: (info) => info.column.id,
        },
        // {
        //     accessorKey: 'labels',
        //     header: 'LABELS',

        //     cell: ({ getValue }) => {
        //         const value = getValue()

        //         // Case 1: Already an array → join and show
        //         if (Array.isArray(value)) {
        //             return value.length ? renderWidgetChips(value) : '-'
        //         }

        //         // Case 2: String (possibly JSON string) → parse & show
        //         if (typeof value === 'string') {
        //             try {
        //                 const parsed = JSON.parse(value)
        //                 return Array.isArray(parsed) ? renderWidgetChips(parsed) : value
        //             } catch {
        //                 // simple string like "label1,label2"
        //                 return value
        //             }
        //         }

        //         return '-'
        //     },
        // },

        {
            accessorKey: 'entities',
            header: 'ENTITIES',
            enableSorting: false,
            cell: ({ row }) => {
                const entities = row.original.entities || []
                
                if (!entities.length) return '-'
                
                const entityNames = entities
                    .map((item: any) => {
                        // Handle different entity types: collection, episode, show
                        return item.collection?.name || item.episode?.name || item.show?.name || item.name
                    })
                    .filter(Boolean)
                
                return entityNames.length ? renderWidgetChips(entityNames) : '-'
            }
        },

        {
            accessorKey: 'categoryPriority',
            header: () => 'Priority',
            footer: (info) => info.column.id,
        },

        // {
        //     accessorKey: 'shortDescription',
        //     header: () => 'Short Description',
        //     cell: ({ getValue }) => {
        //         const value = String(getValue() || '')
        //         return <div className="w-[120px] truncate">{value}</div>
        //     },
        //     footer: (info) => info.column.id,
        // },
        {
            accessorKey: 'createdAt',
            header: 'CREATED DATE',
            cell: ({ getValue }) => {
                const value = getValue() as string | number | Date | null

                return value ? moment(value).format('LL') : '-'
            },
        },
         {
            accessorKey: 'updatedAt',
            header: 'PUBLISH DATE',
            cell: ({ getValue }) => {
                const value = getValue() as string | number | Date | null

                return value ? moment(value).format('LL') : '-'
            },
        },
        {
            id: 'status',
            header: 'STATUS',
            accessorKey: 'status',
            cell: ({ row }) => RenderStatus(row, 'status', onStatusChange),
            enableSorting: false,
        },

        {
            id: 'actions',
            header: 'ACTIONS',
            cell: ({ row }) =>
                RenderDefaultActions(row, ['edit', 'delete', 'view'], onAction),
        },
    ]

    const mobileViewOptions = {
        mainTitle: {
            accessKey: 'name',
        },
    }

    const rowSelectionState = useState<RowSelectionState>({})

    useEffect(() => {
        const subscriber = useFormHook.watch((values, { name }) => {
            if (name) {
                let updated = getUpdatedSearchParams(filterParams, values, name)
                if (Object.keys(updated)?.length) {
                    let sp = encodeURIComponent(JSON.stringify(updated))
                    setSearchParams({ filter: sp })
                } else {
                    setSearchParams({})
                }
            }
        })
        return () => subscriber.unsubscribe()
    }, [useFormHook, filterParams, setSearchParams])

    const filterOptions: FilterOptions[] = [
        {
            key: 'status',
            options: [
                {
                    display: `All`,
                    value: '',
                },
                {
                    display: `Active`,
                    value: 1,
                },
                {
                    display: `Inactive`,
                    value: 0,
                },
            ],
            label: 'Status',
        },
    ]
    useEffect(() => {
        const handlePrintScreen = (e) => {
            if (e.key === 'PrintScreen') {
                navigator.clipboard.writeText('')
                alert('Screenshots disabled!')
            }
        }

        const handleCtrlP = (e) => {
            if (e.ctrlKey && e.key === 'p') {
                alert('This section is not allowed to print or export to PDF')
                e.preventDefault()
                e.stopPropagation()
            }
        }

        document.addEventListener('keyup', handlePrintScreen)
        document.addEventListener('keydown', handleCtrlP)

        return () => {
            document.removeEventListener('keyup', handlePrintScreen)
            document.removeEventListener('keydown', handleCtrlP)
        }
    }, [])

    return isError || isSuccess ? (
        <SmoothTransition>
            {(error as any)?.response?.data?.statusCode === 403 ? (
                <Unauthorized />
            ) : (
                <div className="w-full px-6 pt-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-800 shadow-md ">
                    <ViewCategoryDialog isOpen={openView}
                        setIsOpen={setOpenView}
                        categoryData={viewData} />

                    <ConflictDetailsDrawer
                        isOpen={!!deleteConflict}
                        onClose={() => setDeleteConflict(null)}
                        title="Cannot Delete Category"
                        description={deleteConflict?.message ?? ''}
                        details={deleteConflict?.details ?? []}
                    />

                    <div className="mb-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                                {Object.keys(rowSelectionState[0] || {}).length} selected
                            </span>
                        </div>
                        <button
                            onClick={handleBulkLabelGeneration}
                            disabled={Object.keys(rowSelectionState[0] || {}).length === 0 || isGenerating}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                            <SparklesIcon className="w-4 h-4" />
                            {isGenerating ? 'Generating...' : 'Generate Labels'}
                        </button>
                    </div>

                    <aside className="flex flex-auto items-center">
                        <CustomTable
                            title="Category"
                            titleLabel={
                                !isError
                                    ? `Last fetched on ${getTimeRelativeTime(
                                        dataUpdatedAt
                                    )}`
                                    : ''
                            }
                            formHook={useFormHook}
                            columns={columns}
                            data={responseData?.results}
                            pages={responseData?.pages}
                            totalRecords={responseData?.total}
                            onRefresh={refetch}
                            isLoading={isLoading}
                            isErrored={isError}
                            rowSelectionState={rowSelectionState}
                            mobileViewOptions={mobileViewOptions}
                            useFieldForSelect="id"
                            filterOptions={filterOptions}
                            onAddAction={() => navigate(`/category/add`)}
                        />
                    </aside>
                </div>
            )}
        </SmoothTransition>
    ) : (
        <PagePreloader />
    )
}

export default CategoryList
